import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useBasePath } from '../../lib/use-base-path';
import { useIslandState } from '../../lib/use-island-state';
import { useGenericBi } from '../../lib/use-generic-bi';
import { handleModalEscape, pushModalEscape } from '../../lib/modal-escape';
import { useIslandQuickView } from '../../lib/use-island-quickview';
import type { QuickViewTask } from '../../lib/island-quickview';
import type { AgentTaskBucket } from '../../lib/agent-task-buckets';
import { useMyAgents } from '../../lib/my-agents';
import { AgentDot } from '../AgentDot';
import { useTaskSidePanel } from './task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from './tasks/TaskDetailModal';
import { OutreachThreadModal } from './tasks/OutreachThreadModal';
import { queryClient, queryKeys } from '../../lib/query-client';
import type { WebTask } from '../../lib/tasks';
import { Button, StatusBadge, ScrollArea } from './ui';

// The island quick-view — the "1c: stretch, then drop" morph, where the PILL IS THE PANEL'S HEADER. One
// continuous tinted surface: the pill (agent dots + state count) widens across the bar into the panel's
// full-width header row FIRST (fast, no delay), THEN the body drops open beneath it (height, delayed, with
// an overshoot), and only THEN do the task rows fade + slide in. The pill's content stays pinned to the
// trailing edge so it never slides; the standalone pill is hidden while open, so the header stands in for
// it seamlessly. Closing runs the same curve without overshoot and ~25% faster: the body collapses first,
// the header shrinks back to the pill last. Rows open the task; the footer is the app's standard dark CTA.

const INK = 'var(--sym-ink)';
const INK_2 = 'var(--ds-text-secondary)';

const PANEL_W = 335;
const EDGE_GAP = 8; // the panel never sits closer than this to a viewport edge
/** The panel's own box (Figma 13523-215388): its edge, its sides and foot, where content starts, and the gap
 * between blocks. */
const PANEL_BORDER = 1;
const PANEL_PAD = 20;
const CONTENT_TOP = 48;
const CONTENT_GAP = 20;

/** Which edge the panel is pinned to — and therefore which edge its header content hugs. */
export type IslandSide = 'left' | 'right' | 'center';
const HEADER_JUSTIFY: Record<IslandSide, 'flex-start' | 'flex-end' | 'center'> = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};
const RADIUS_OPEN = 20;
const CLOSE_MS = 340; // hold the node mounted through the close curve before unmounting
const OS = 'cubic-bezier(.34,1.34,.38,1)'; // overshoot (open)
const EO = 'cubic-bezier(.35,0,.22,1)'; // ease-out (close)
const OPEN_TR = `width 167ms cubic-bezier(.3,0,.2,1), max-height 266ms ${OS} 122ms, background-color 266ms ease 122ms, border-radius 380ms ease, box-shadow 380ms ease`;
const CLOSE_TR = `width 126ms ${EO} 141ms, max-height 169ms ${EO}, background-color 169ms ${EO}, border-radius 281ms ease, box-shadow 281ms ease`;
const BODY_OPEN_TR = `opacity 200ms ease 274ms, transform 260ms ${OS} 274ms`;
const BODY_CLOSE_TR = 'opacity 90ms ease, transform 90ms ease';
const SHADOW_LIFT = '0 16px 36px rgba(var(--ds-black-rgb), 0.15), 0 2px 6px rgba(var(--ds-black-rgb), 0.06)';
const SHADOW_SHUT = '0 2px 6px rgba(var(--ds-black-rgb), 0.07)';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function SkeletonRow() {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px', alignItems: 'flex-start' }}>
      <span className="orion-shimmer" style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="orion-shimmer" style={{ height: 12, width: '75%', borderRadius: 6 }} />
        <span className="orion-shimmer" style={{ height: 10, width: '45%', borderRadius: 6 }} />
      </span>
    </div>
  );
}

/** Between rows only — the first row sits flush under the bucket control. Separator/Light (Figma 13523-215388). */
const HAIRLINE: React.CSSProperties = { borderTop: '1px solid var(--ds-separator-default)' };

/**
 * The bucket control: a row carrying this state's count on the state's OWN badge fill, tapping through to
 * the account-wide Tasks list on the matching tab. Its text is the same `Text & Icon/Primary` everything
 * else on the panel uses.
 */
function bucketRowStyle(fill: string, textColor: string): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    padding: '12px 12px 12px 16px',
    minHeight: 36,
    borderRadius: 16,
    border: 'none',
    background: fill,
    color: textColor,
    font: 'inherit',
    fontSize: 16,
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
  };
}

const META_LINE: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: INK_2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function Row({
  task,
  agentName,
  onOpen,
}: {
  task: QuickViewTask;
  agentName?: string;
  onOpen: (task: QuickViewTask) => void;
}) {
  const { t } = useTranslation();
  // An outreach row is titled by WHO SENT IT — its subject is a person, not a piece of work. Same rule,
  // same copy keys as the list's `TaskCard`, because this row is a peek at that list.
  const title = task.isOutreach
    ? task.senderName
      ? t('tasks.outreach.title', { name: task.senderName })
      // "Incoming message", not "Untitled task" — an inbound outreach whose sender could not be resolved
      // is neither a task nor untitled, and the phones already said so. Same key, same 37 translations.
      : t('tasks.outreach.untitled')
    : task.title || t('tasks.untitledTask');
  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        padding: '10px',
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      {/* 14/18 rather than the frame's 14/14: an ellipsizing run needs a line box taller than its own
          glyphs, or `overflow: hidden` shaves the descenders. */}
      <span style={{ fontSize: 14, fontWeight: 'var(--sym-weight-medium)', lineHeight: '18px', letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {/* The agent tag grows to fill the row so the status badge is pushed to the trailing (right) edge;
            `minWidth: 0` lets the name ellipsize inside it rather than shoving the badge off-row. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          {task.agentId ? <AgentDot id={task.agentId} size={12} /> : null}
          {agentName ? <span style={META_LINE}>{agentName}</span> : null}
        </span>
        <StatusBadge status={task.status} />
      </span>
    </button>
  );
}

export function AgentsIslandQuickView({
  anchorEl,
  open,
  tone,
  side,
  headerContent,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  /** The pill's current colour — the surface tints to match so it reads as the pill's own panel. */
  tone: 'blue' | 'orange';
  /** Which way the panel opens — declared by the mount (see `AgentsIslandBadge`). */
  side: IslandSide;
  /** The pill's own inner content, rendered as the panel's header row so the two are one surface. */
  headerContent: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openModal } = useTaskSidePanel();
  const router = useRouter();
  const basePath = useBasePath();
  const island = useIslandState();
  const state = useIslandQuickView();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; offset: number; pillW: number; pillH: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  // The body's natural height (rows + footer), so max-height eases 1:1 with the real drop.
  const [bodyH, setBodyH] = useState(0);

  const place = useCallback(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    // Overlay the pill exactly, then grow AWAY from the side it sits on: a LEFT island pins its left edge and
    // opens rightwards, a RIGHT one pins its right edge and opens leftwards, and a CENTRED one holds its
    // midpoint and opens symmetrically. Native derives this from the pill's midpoint across a phone screen;
    // here the mount declares it, because a web pill inset by the content gutter can sit mid-viewport while
    // plainly belonging to the right-hand edge.
    //
    // The offset is whichever edge is PINNED, so the growing width moves the OTHER edge and the shape starts
    // exactly on the pill. Pinning the wrong one is what made a right-hand island appear a panel-width away
    // and slide over: with `left` fixed, `width: pill → panel` grows the right edge away from the pill.
    const mid = r.left + r.width / 2;
    const raw = side === 'left' ? r.left : side === 'right' ? window.innerWidth - r.right : mid - PANEL_W / 2;
    // Never past either edge — the same clamp native applies, so a pill near a corner still lands on screen.
    const room = Math.max(EDGE_GAP, window.innerWidth - PANEL_W - EDGE_GAP);
    const offset = side === 'center' ? mid : Math.min(Math.max(raw, EDGE_GAP), room);
    setPos({ top: r.top, offset, pillW: r.width, pillH: r.height });
  }, [anchorEl, side]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Re-anchoring updates state on every scroll frame, so the listener effect below must not depend on a
  // caller's inline closure — it would tear its listeners down and re-register them each frame.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const close = useCallback(() => onCloseRef.current(), []);
  // Closing because the PILL left the viewport skips the close curve entirely. The curve collapses the panel
  // back onto its anchor, and there is no anchor on screen to collapse onto — so animating it just parks a
  // pill-shaped capsule over the page content for the length of the animation, which is the stray island
  // reported while scrolling. Everything else (a tap, Escape, a row) still gets the full morph.
  const vanish = useCallback(() => {
    setShown(false);
    setMounted(false);
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    setShown(false);
    const id = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // `pointerdown`, so a tap outside dismisses on a touch screen at the same moment a click does on a mouse.
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !anchorEl?.contains(target)) close();
    };
    const release = pushModalEscape(close);
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      release();
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, anchorEl, close]);

  // The panel is the PILL'S OWN surface, so it RE-ANCHORS as the page moves rather than being left behind.
  // Gated on `mounted`, NOT on `open`, because the close curve collapses the panel back ONTO its anchor: stop
  // tracking the moment it starts closing and the rest of the scroll strands it, which is the second island
  // seen mid-page — the pill is already back while a capsule finishes shrinking where the pill used to be.
  // Scrolling the panel's own list is not the page moving and is ignored.
  useEffect(() => {
    if (!mounted) return undefined;
    let frame = 0;
    const onReflow = (e?: Event) => {
      if (e?.type === 'scroll') {
        const target = e.target as Node | null;
        if (target && panelRef.current?.contains(target)) return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = anchorEl?.getBoundingClientRect();
        // Nothing on screen left to anchor to, so there is nothing to animate towards either.
        if (!r || r.bottom <= 0 || r.top >= window.innerHeight) {
          vanish();
          return;
        }
        place();
      });
    };
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [mounted, anchorEl, vanish, place]);

  // Keep the measured body height current — including a loading→ready swap while open.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => setBodyH(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state.status, mounted]);

  if (!mounted || !pos || state.status === 'error') return null;
  const loading = state.status === 'loading';
  const rows = state.status === 'ready' ? state.data.rows : [];
  const agentName = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.name : undefined);

  // The bucket control's destination: the account-wide Tasks list on THIS state's tab. `?tab=` takes the
  // camelCase union value `parseBucketParam` accepts — `in-progress` is the BI token only, and sending it
  // here would be silently ignored and land on the default tab.
  const openBucketTab = () => {
    const tab: AgentTaskBucket = tone === 'orange' ? 'waiting' : 'inProgress';
    logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'open-bucket-tab' });
    onClose();
    void router.push({ pathname: '/tasks', query: { tab } }, `${basePath}/tasks?tab=${tab}`);
  };

  // Open the row in the shared modal — the SAME drill-in the "All tasks" list opens (TaskCard →
  // useTaskSidePanel.openModal) — rather than navigating to the /tasks/[id] page. Closing the island
  // first, then opening the modal, leaves the current page untouched, so dismissing the modal returns
  // here rather than to a pushed page.
  //
  // An inbound outreach has no AgentTask behind it, so there is nothing for a task detail to read; it
  // opens its THREAD in place, exactly as the list card does. The full row is taken from the account-wide
  // cache the rows were built from (the thread needs its messages, which the pure row shape doesn't
  // carry) — a read, not a new observer, and it is populated by definition since this row came from it.
  const onOpen = (task: QuickViewTask) => {
    const outreach = task.isOutreach
      ? queryClient.getQueryData<WebTask[]>(queryKeys.tasks(undefined))?.find((t) => t.id === task.id)
      : undefined;
    // `open_task` / `open_outreach` — the SAME src:172 button_name the Tasks list's own card reports for
    // the same action (`TaskCard`), deliberately NOT the island's local kebab style. src:172 is a
    // web-only event whose vocabulary is snake_case, and a query for `open_task` that excludes island
    // opens undercounts the action rather than failing visibly. (The generic src:198 event is unaffected
    // either way — `logPageClickBi` kebabs `button_name` into `componentName`, which is what joins to the
    // phones' `kebabCase` output, so both spellings already emitted `open-task` there.)
    logPageClickBi({
      page_name: 'agents-island-quickview',
      button_name: outreach ? 'open_outreach' : 'open_task',
      task_id: task.id,
    });
    onClose();
    openModal(outreach ? <OutreachThreadModal task={outreach} /> : <TaskDetailModal taskId={task.id} />, task.id);
  };

  const variant = tone === 'orange' ? 'waiting-on-you' : 'in-progress';
  // The OPEN panel sits one shade lighter than the closed pill in LIGHT mode, and takes the pill's own
  // deep fill in DARK — the phones' `islandQuickView*Bg` exactly. So the surface lifts as it opens
  // instead of the panel and the pill reading as one flat block of colour.
  //
  // These are the island's OWN roles, not the generic badge tokens. A badge fill LIGHTENS in dark
  // (Badge/Fill/In Progress #E1E9FB → #A0B8F2) while the ink on it flips to near-white, which is what
  // made the task titles vanish into the card. See the island block in globals.css.
  //
  // It CROSSES between the two rather than switching: the colour rides the same leg as the height (see the
  // transitions above), which is native's `lerp(pillFill, quickViewBg, heightP)`. Switching outright read as a
  // glitch — on close the panel finished collapsing and the colour then jumped back a beat later.
  const openTint = tone === 'orange'
    ? 'var(--island-quickview-waiting-bg)'
    : 'var(--island-quickview-in-progress-bg)';
  const pillFill = tone === 'orange' ? 'var(--island-waiting-bg)' : 'var(--island-in-progress-bg)';
  const tint = shown ? openTint : pillFill;
  // Same split as the pill: the state colour draws the panel's EDGE, never its text. Everything written on
  // the tinted surface is `Text & Icon/Primary` (black), so the open panel reads like the closed pill.
  const edge = tone === 'orange' ? 'var(--island-waiting-border)' : 'var(--island-in-progress-border)';
  const reduce = prefersReducedMotion();
  // The panel's box is the pill's footprint, but the header sits INSIDE the panel's edge — so its own box is
  // that footprint less both borders. Given the pill's full height it starts a pixel low, and the pill's text
  // drops 1px the moment the panel opens and hops back as it closes.
  const headerH = pos.pillH - PANEL_BORDER * 2;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('agents.tasksHeading', { count: rows.length })}
      style={{
        position: 'fixed',
        top: pos.top,
        ...(side === 'right' ? { right: pos.offset } : { left: pos.offset }),
        // Centred: hold the MIDPOINT rather than an edge, so the width grows evenly on both sides.
        transform: side === 'center' ? 'translateX(-50%)' : undefined,
        // Above the chat surface (50) and its resize handle (60) — the panel is portalled to <body>, so in
        // fullscreen chat a lower layer put it BEHIND the chat and clicking the pill appeared to do nothing
        // (the pill hides itself while open, so it just vanished). Still far below a real modal (1000), which
        // has to cover this when a row opens a task.
        zIndex: 70,
        boxSizing: 'border-box',
        // Stretch (width) → drop (max-height). Closed = the pill footprint (header only); open = the panel.
        width: shown ? PANEL_W : pos.pillW,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: shown ? pos.pillH + bodyH : pos.pillH,
        overflow: 'hidden',
        background: tint,
        border: `${PANEL_BORDER}px solid ${edge}`,
        borderRadius: shown ? RADIUS_OPEN : pos.pillH / 2,
        boxShadow: shown ? SHADOW_LIFT : SHADOW_SHUT,
        transition: reduce ? 'none' : shown ? OPEN_TR : CLOSE_TR,
      }}
    >
      {/* The header IS the pill: its content pinned to the trailing edge so it never slides as the shape
          widens. Clicking it closes the panel (the pill's own toggle). */}
      <div
        onClick={onClose}
        style={{
          height: headerH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: HEADER_JUSTIFY[side],
          gap: 8,
          // Sits on the card's 20px side inset (Figma 13523-215388).
          padding: `0 ${PANEL_PAD}px`,
          cursor: 'pointer',
          color: INK,
        }}
      >
        {headerContent}
      </div>

      {/* The body drops beneath the header and its content fades + slides in last. */}
      <div
        ref={bodyRef}
        style={{
          width: PANEL_W,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: CONTENT_GAP,
          // The content starts 48px down from the panel's top edge, whatever the pill measured — so the
          // header's own height is spent out of that inset rather than added to it.
          padding: `${Math.max(0, CONTENT_TOP - headerH)}px ${PANEL_PAD}px ${PANEL_PAD}px`,
          opacity: shown ? 1 : 0,
          transform: shown ? 'translateY(0)' : 'translateY(-8px)',
          transition: reduce ? 'none' : shown ? BODY_OPEN_TR : BODY_CLOSE_TR,
        }}
      >
        {/* The bucket row — the panel's own nav control, above the list. Its count is the GLOBAL in-motion /
            waiting aggregate the pill itself reads, never `rows.length`: the rows are the recent-tasks peek
            across every status, so counting them here would state a different number than the pill. */}
        <button
          type="button"
          onClick={openBucketTab}
          style={bucketRowStyle(
            tone === 'orange' ? 'var(--island-nav-pill-waiting-bg)' : 'var(--island-nav-pill-in-progress-bg)',
            INK,
          )}
        >
          <span>
            {t(tone === 'orange' ? 'agentsIsland.quickView.tasksWaiting' : 'agentsIsland.quickView.tasksInProgress', {
              count: tone === 'orange' ? island.waitingCount : island.inMotionCount,
            })}
          </span>
          <ChevronRight size={18} aria-hidden className="dir-flip" />
        </button>

        {/* Native's card does not scroll at all: six two-line rows simply fit. The cap here is a viewport
            guard for a short window, not a design height — sized so the full six never reach it. */}
        <ScrollArea style={{ maxHeight: 'min(70vh, 560px)' }}>
          {(loading ? [0, 1, 2] : rows).map((row, i) => (
            <div key={typeof row === 'number' ? row : row.id} style={i > 0 ? HAIRLINE : undefined}>
              {typeof row === 'number' ? (
                <SkeletonRow />
              ) : (
                <Row task={row} agentName={agentName(row.agentId)} onOpen={onOpen} />
              )}
            </div>
          ))}
        </ScrollArea>

        <div>
          <Button
            href="/tasks"
            fullWidth
            onClick={() => {
              logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'see-all-tasks' });
              onClose();
            }}
          >
            {t('agentsIsland.quickView.viewAllTasks')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
